import React, { useEffect, useRef, useState } from "react";
import { FileOutlined } from '@ant-design/icons';
import { notification } from "antd";
import { LinearProgress } from "@material-ui/core";
import { useDispatch } from "react-redux";
import styles from "./JobSwmsV2.module.css";
// import PreviewSwmsV2 from "../JobSwmsPreview/PreviewSwmsV2";
import SignHistoryV2 from "../JobSignHistory/SignHistoryV2";
import JobSwmsTasks from "./JobSwmsTasks";
import SwmsCard from "./SwmsCard";
import SwmsWizard from "./SwmsWizard";
import SwmsSendApprovalModal from "./SwmsSendApprovalModal";
import SwmsCopyConfirmModal from "./SwmsCopyConfirmModal";
import SwmsVersionHistoryModal from "./SwmsVersionHistoryModal";
import SwmsDeleteConfirmModal from "./SwmsDeleteConfirmModal";
import SwmsCancelApprovalConfirmModal from "./SwmsCancelApprovalConfirmModal";
import SwmsVisibilityConfirmModal from "./SwmsVisibilityConfirmModal";
import SwmsSendClientModal from "./SwmsSendClientModal";
import JobShiftPreStartOverlay from "../JobShiftPreStart/JobShiftPreStartOverlay";
import JobShiftDocumentPlaceholderOverlay from "../JobShiftPreStart/JobShiftDocumentPlaceholderOverlay";
import JobShiftReadinessOverlay from "../JobShiftReadiness/JobShiftReadinessOverlay";
import { getAllJobSwms } from "../../../../../actions/v2/JobSwmsTaskActions";
import { Strings } from "../../../../../dataProvider/localize";
import PreviewV3Container from "../JobSwmsPreview/v3/PreviewV3Container";

const mapSwmsDraftToCard = (swmsDraft) => {
  const getFirstValue = (values, fallback) => {
    const firstValue = values.find((value) => value !== undefined && value !== null && value !== "");
    return firstValue !== undefined && firstValue !== null ? firstValue : fallback;
  };

  const normalizeNamedItems = (items, nameKeys = [], idKeys = []) => {
    if (!Array.isArray(items)) return [];

    return items.map((item) => {
      if (typeof item === "string") {
        return { id: null, name: item };
      }

      if (!item || typeof item !== "object") {
        return { id: null, name: "" };
      }

      const name = getFirstValue(nameKeys.map((key) => item[key]), "");
      const id = getFirstValue(idKeys.map((key) => item[key]), null);

      return { id, name };
    }).filter((item) => item.name);
  };

  const approvers = Array.isArray(swmsDraft.approversSummary)
    ? swmsDraft.approversSummary.map((approver) => getFirstValue([approver.signerName, approver.name, approver.userId], ""))
    : [];

  const approversSummary = Array.isArray(swmsDraft.approversSummary)
    ? swmsDraft.approversSummary.map((approver) => ({
      name: getFirstValue([approver.signerName, approver.name, approver.userId], ""),
      status: getFirstValue(
        [approver.signerStatus, approver.status, approver.currentStatus, approver.approvalStatus, approver.responseStatus],
        "PENDING"
      )
    }))
    : [];

  const formId = getFirstValue([swmsDraft.swmsId, swmsDraft.PK, swmsDraft.SK], "ID-0000");
  const formNumber = getFirstValue([swmsDraft.swmsNumber, swmsDraft.swms_number], formId);
  const formName = getFirstValue([swmsDraft.title], "Untitled SWMS");
  const approvalStatus = getFirstValue([swmsDraft.currentStatus], "Draft");
  const createdAt = getFirstValue([swmsDraft.createdAt, swmsDraft.updatedAt], null);
  const createdBy = getFirstValue([swmsDraft.createdByName, swmsDraft.createdBy], "N/A");
  const currentVersion = getFirstValue([swmsDraft.currentVersion, swmsDraft.current_version], 0);
  const jobNumber = getFirstValue([swmsDraft.jobNumber], "");
  const jobLabel = getFirstValue([swmsDraft.jobId], "N/A");
  const siteName = getFirstValue([swmsDraft.siteName, jobNumber], `Job ${jobLabel}`);
  const categories = normalizeNamedItems(swmsDraft.categories, ["name", "category_name"], ["id", "category_id"]);
  const methods = normalizeNamedItems(swmsDraft.methods, ["name", "method_name"], ["id", "method_id"]);
  const tasks = normalizeNamedItems(swmsDraft.tasks, ["name", "task_name", "display_name"], ["id", "task_id"]);

  return {
    swms_form_id: formId,
    swms_number: formNumber,
    swms_form_name: formName,
    approval_status: approvalStatus,
    created_at: createdAt,
    created_by: createdBy,
    currentVersion,
    is_visible: swmsDraft.siteVisible === true ? 1 : 0,
    site_name: siteName,
    categories,
    methods,
    tasks,
    approvers,
    approversSummary,
    pk: swmsDraft.PK,
    sk: swmsDraft.SK,
    raw: swmsDraft
  };
};

export default function JobSwmsV2({ goToForm, jobId, sections, setSections }) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobSwmsDrafts, setJobSwmsDrafts] = useState([]);
  const [previewModalSwms, setPreviewModalSwms] = useState(null);
  const [approvalModalSwms, setApprovalModalSwms] = useState(null);
  const [copyModalSwms, setCopyModalSwms] = useState(null);
  const [versionHistoryModalSwms, setVersionHistoryModalSwms] = useState(null);
  const [deleteModalSwms, setDeleteModalSwms] = useState(null);
  const [cancelApprovalModalSwms, setCancelApprovalModalSwms] = useState(null);
  const [visibilityModalState, setVisibilityModalState] = useState(null);
  const [sendClientModalSwms, setSendClientModalSwms] = useState(null);
  const [showPreStartView, setShowPreStartView] = useState(false);
  const [preStartViewContext, setPreStartViewContext] = useState(null);
  const [historyDocumentView, setHistoryDocumentView] = useState(null);
  const [readinessViewContext, setReadinessViewContext] = useState(null);

  const loadJobSwms = () => {
    if (!jobId) {
      setLoading(false);
      setJobSwmsDrafts([]);
      return;
    }

    if (controller.current) {
      controller.current.abort();
    }

    controller.current = new AbortController();
    setLoading(true);
    dispatch(getAllJobSwms({ signal: controller.current.signal }, jobId)).then((res) => {
      const mappedDrafts = Array.isArray(res) ? res.map(mapSwmsDraftToCard) : [];
      setJobSwmsDrafts(mappedDrafts);
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      if (err) {
        notification.error({
          message: Strings.error_title,
          description: err ? err : Strings.generic_error,
          className: "ant-error"
        });
      }
    });
  };

  useEffect(() => {
    loadJobSwms();

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, [dispatch, jobId]);

  const handleCloseWizard = () => {
    setShowWizard(false);
    loadJobSwms();
  };

  const handleWizardCreated = (createdSwms) => {
    setShowWizard(false);
    loadJobSwms();

    const createdSwmsId = createdSwms && createdSwms.swmsId;
    if (!goToForm || !createdSwmsId) {
      return;
    }

    goToForm({
      task: createdSwms,
      version: {
        swms_form_id: createdSwmsId
      }
    });
  };

  const handleOpenPreviewModal = (swms) => {
    if (!swms || !swms.swms_form_id) {
      return;
    }

    setPreviewModalSwms(swms);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalSwms(null);
  };

  const handlePreviewOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClosePreviewModal();
    }
  };

  const handleEditSwms = (swms) => {
    if (!goToForm || !swms || !swms.swms_form_id) {
      return;
    }

    goToForm({
      task: swms.raw ? swms.raw : swms,
      version: {
        swms_form_id: swms.swms_form_id
      }
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalSwms(null);
    loadJobSwms();
  };

  const handleCloseCopyModal = () => {
    setCopyModalSwms(null);
    loadJobSwms();
  };

  const handleOpenVersionHistoryModal = (swms) => {
    if (!swms) {
      return;
    }

    setVersionHistoryModalSwms(swms);
  };

  const handleCloseVersionHistoryModal = () => {
    setVersionHistoryModalSwms(null);
  };

  const handleCloseCancelApprovalModal = () => {
    setCancelApprovalModalSwms(null);
  };

  const handleCancelApprovalSuccess = () => {
    setCancelApprovalModalSwms(null);
    loadJobSwms();
  };

  const handleOpenVisibilityModal = (swms, targetVisible) => {
    if (!swms) {
      return;
    }

    setVisibilityModalState({ swms, targetVisible: targetVisible === true });
  };

  const handleCloseVisibilityModal = () => {
    setVisibilityModalState(null);
    loadJobSwms();
  };

  const handleOpenPreStartView = (viewContext) => {
    if (!viewContext || !viewContext.swmsId || !viewContext.shiftId) {
      return;
    }

    setPreStartViewContext({
      ...viewContext,
      jobId
    });
    setShowPreStartView(true);
  };

  const handleClosePreStartView = () => {
    setShowPreStartView(false);
  };

  const handleOpenHistoryDocumentView = (title, viewContext) => {
    if (!viewContext || !viewContext.swmsId || !viewContext.shiftId) {
      return;
    }

    setHistoryDocumentView({
      title,
      context: {
        ...viewContext,
        jobId
      }
    });
  };

  const handleCloseHistoryDocumentView = () => {
    setHistoryDocumentView(null);
  };

  const handleOpenEmergencyPlans = (viewContext) => {
    handleOpenHistoryDocumentView("Emergency Plans", viewContext);
  };

  const handleOpenEmergencyProcedures = (viewContext) => {
    handleOpenHistoryDocumentView("Emergency Procedures", viewContext);
  };

  const handleOpenRescueReadiness = (viewContext) => {
    if (!viewContext || !viewContext.swmsId || !viewContext.shiftId) {
      return;
    }

    setReadinessViewContext({
      ...viewContext,
      jobId
    });
  };

  const handleCloseReadinessView = () => {
    setReadinessViewContext(null);
  };

  const handleDownloadWhsPack = (viewContext) => {
    const zipLink = viewContext
      && viewContext.whsPack
      && viewContext.whsPack.whs_pack
      && viewContext.whsPack.whs_pack.zip_link;

    if (zipLink) {
      window.open(zipLink, "_blank", "noopener,noreferrer");
      return;
    }

    notification.info({
      message: "WHS Pack",
      description: "The WHS Pack is not ready for download yet.",
      className: "ant-info"
    });
  };

  const swmsCards = jobSwmsDrafts;

  return (
    <div className={styles.pageContainer}>
      <hr className={styles.topDivider} />
      <div className={styles.progressBarSlot}>
        {loading && <LinearProgress />}
      </div>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button className={styles.createButton} onClick={() => setShowWizard(true)}>
          Create New SWMS
        </button>
      </div>

      {/* SWMS Cards Grid */}
      {swmsCards.length > 0 && (
        <div className={styles.cardGrid}>
          {swmsCards.map((swms, index) => (
            <SwmsCard
              key={index}
              swms={swms}
              onView={handleOpenPreviewModal}
              onSendForApproval={setApprovalModalSwms}
              onSendToClient={setSendClientModalSwms}
              onEdit={handleEditSwms}
              onCopy={setCopyModalSwms}
              onVersionHistory={handleOpenVersionHistoryModal}
              onDelete={setDeleteModalSwms}
              onCancelReroute={setCancelApprovalModalSwms}
              onToggleVisibility={handleOpenVisibilityModal}
            />
          ))}
        </div>
      )}
      {!loading && swmsCards.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FileOutlined />
          </div>
          <h3 className={styles.emptyStateTitle}>No SWMS documents yet</h3>
          <p className={styles.emptyStateText}>Get started by creating your first Safe Work Method Statement for this job.</p>
          <button className={styles.createButton} onClick={() => setShowWizard(true)}>
            Create New SWMS
          </button>
        </div>
      )}

      {/* SWMS Tasks List */}
      <JobSwmsTasks goToForm={goToForm} jobId={jobId} sections={sections} setSections={setSections} />

      {/* Sign History */}
      <hr className={styles.topDivider} style={{ marginTop: "32px" }} />
      <SignHistoryV2
        jobId={jobId}
        onOpenPreStart={handleOpenPreStartView}
        onOpenEmergencyPlans={handleOpenEmergencyPlans}
        onOpenEmergencyProcedures={handleOpenEmergencyProcedures}
        onOpenRescueReadiness={handleOpenRescueReadiness}
        onOpenWhsPack={handleDownloadWhsPack}
      />

      {/* Wizard */}
      {showWizard && (
        <SwmsWizard jobId={jobId} onClose={handleCloseWizard} onCreated={handleWizardCreated} />
      )}

      {/* Preview SWMS Modal */}
      {previewModalSwms && (
        <PreviewV3Container
          previewJobId={jobId}
          swms={previewModalSwms}
          onClose={handleClosePreviewModal}
        />
        // <div className={styles.previewOverlay} onClick={handlePreviewOverlayClick}>
        //   <div className={styles.previewModal}>
        //     <div className={styles.previewBody}>
        //       <PreviewSwmsV2
        //         swms={previewModalSwms}
        //         task={{ job_id: jobId, jobId }}
        //         onClose={handleClosePreviewModal}
        //       />
        //     </div>
        //   </div>
        // </div>
      )}

      {/* Send for Approval Modal */}
      {approvalModalSwms && (
        <SwmsSendApprovalModal
          swms={approvalModalSwms}
          jobId={jobId}
          onClose={() => setApprovalModalSwms(null)}
          onSuccess={loadJobSwms}
        />
      )}

      {/* Copy SWMS Modal */}
      {copyModalSwms && (
        <SwmsCopyConfirmModal
          swms={copyModalSwms}
          jobId={jobId}
          onClose={handleCloseCopyModal}
        />
      )}

      {/* Version History Modal */}
      {versionHistoryModalSwms && (
        <SwmsVersionHistoryModal
          swms={versionHistoryModalSwms}
          jobId={jobId}
          onClose={handleCloseVersionHistoryModal}
          onSuccess={loadJobSwms}
        />
      )}

      {/* Delete SWMS Modal */}
      {deleteModalSwms && (
        <SwmsDeleteConfirmModal
          swms={deleteModalSwms}
          jobId={jobId}
          onClose={handleCloseDeleteModal}
        />
      )}

      {/* Cancel Approval Modal */}
      {cancelApprovalModalSwms && (
        <SwmsCancelApprovalConfirmModal
          swms={cancelApprovalModalSwms}
          jobId={jobId}
          onClose={handleCloseCancelApprovalModal}
          onSuccess={handleCancelApprovalSuccess}
        />
      )}

      {/* Site Visibility Modal */}
      {visibilityModalState && (
        <SwmsVisibilityConfirmModal
          swms={visibilityModalState.swms}
          jobId={jobId}
          targetVisible={visibilityModalState.targetVisible}
          onClose={handleCloseVisibilityModal}
        />
      )}

      {/* Send to Client Modal */}
      {sendClientModalSwms && (
        <SwmsSendClientModal
          swms={sendClientModalSwms}
          jobId={jobId}
          onClose={() => setSendClientModalSwms(null)}
          onSuccess={loadJobSwms}
        />
      )}

      <JobShiftPreStartOverlay
        open={showPreStartView}
        context={preStartViewContext}
        onClose={handleClosePreStartView}
      />

      <JobShiftDocumentPlaceholderOverlay
        open={Boolean(historyDocumentView)}
        title={historyDocumentView ? historyDocumentView.title : ""}
        context={historyDocumentView ? historyDocumentView.context : null}
        onClose={handleCloseHistoryDocumentView}
      />

      <JobShiftReadinessOverlay
        open={Boolean(readinessViewContext)}
        context={readinessViewContext}
        onClose={handleCloseReadinessView}
      />
    </div>
  );
}
